import React, { useEffect } from 'react';
import { useLocation, matchPath } from 'react-router';
import { connect } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import {
  Drawer,
  List,
  ListSubheader,
  Hidden,
  Typography,
} from '@material-ui/core';

import { Link as RouterLink } from 'react-router-dom';

import { getIsSubApp } from '~/selectors';
import navConfig from '../navConfig';
import NavItem from '../../../components/NavItem/NavItem';
import packageJSON from '../../../../../package.json';
import styles from './styles';

const { version } = packageJSON;

function renderNavItems({
  items,
  key,
  subheader,
  unreadCount,
  isNotified,
  ...rest
}) {
  return (
    <List key={key}>
      {subheader && <ListSubheader disableSticky>{subheader}</ListSubheader>}
      {/* eslint-disable-next-line react/prop-types */}
      {items.reduce(
        (acc, item, i) =>
          // eslint-disable-next-line no-use-before-define
          reduceChildRoutes({
            acc,
            item,
            key: i,
            unreadCount,
            isNotified,
            ...rest,
          }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc,
  key,
  pathname,
  item,
  depth = 0,
  unreadCount,
  isNotified,
}) {
  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false,
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        customIcon={item.customIcon}
        key={key}
        label={item.label}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
          key: `${depth}_${key}`,
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        customIcon={item.customIcon}
        key={key}
        target={item.target}
        label={item.label}
        title={item.title}
        badge={item.title === 'Chats' && unreadCount > 0 ? unreadCount : null}
        notificationBadge={item.title === 'Chats' && isNotified ? '!' : null}
        className={item.className}
      />
    );
  }

  return acc;
}

function NavBar({
  openMobile,
  onMobileClose,
  className,
  unreadCount,
  isNotified,
  isSubApp,
}) {
  const classes = styles();
  const location = useLocation();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  const content = (
    <div className={clsx(classes.root, className)}>
      <RouterLink
        style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
        to="/channels"
      >
        {!isSubApp && (
          <img
            style={{ margin: '32px 0' }}
            alt="Logo"
            src="https://sms.messagewhiz.com/static/1e027d0d5e03877f785cb1979c9c506d.svg"
          />
        )}
      </RouterLink>
      <nav className={classes.navigation}>
        {navConfig.map((list, index) =>
          renderNavItems({
            unreadCount,
            isNotified,
            items: list.items,
            subheader: list.subheader,
            pathname: location.pathname,
            target: list.target,
            className: list.className,
            key: `${list.subheader}_${index}`,
          })
        )}
      </nav>
    </div>
  );

  const PaperProps = {
    id: 'navbar-drawer-container',
    style: {
      position: 'absolute',
    },
    classes: {
      root: classes.paperRoot,
    },
  };

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{
            paper: classes.mobileDrawer,
          }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
          PaperProps={PaperProps}
        >
          {content}
          {!isSubApp && (
            <div style={{ padding: 8 }}>
              <div>
                <Typography className={classes.appVersion} variant="h6">
                  v{version}
                </Typography>
              </div>
            </div>
          )}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{
            paper: classes.desktopDrawer,
          }}
          open
          variant="persistent"
          PaperProps={PaperProps}
        >
          {content}
          {!isSubApp && (
            <div style={{ padding: 8 }}>
              <div>
                <Typography className={classes.appVersion} variant="h6">
                  v{version}
                </Typography>
              </div>
            </div>
          )}
        </Drawer>
      </Hidden>
    </>
  );
}

NavBar.propTypes = {
  className: PropTypes.string,
  onMobileClose: PropTypes.func.isRequired,
  openMobile: PropTypes.bool.isRequired,
  isSubApp: PropTypes.bool.isRequired,
};

NavBar.defaultProps = {
  className: '',
};

export default connect((state) => {
  const { unread_messages, notificationStatus } = state;

  return {
    unreadCount: Object.values(unread_messages).reduce(
      (sum, unread) => sum + unread,
      0
    ),
    isNotified: Object.values(notificationStatus).some(Boolean),
    isSubApp: getIsSubApp(state),
  };
})(NavBar);
